import { localStorageManager } from "@/services/localStorage";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";

const STORAGE_KEY = LOCAL_STORAGE_BASE_KEYS.SEEN_AUTOMATION_TOAST_KEYS;

/**
 * Parses the stored seen-toast record, dropping anything already expired.
 *
 * Every malformed shape yields an empty map rather than a throw or a partial
 * guess. That direction is deliberate: an empty map reproduces the
 * pre-persistence behavior (a toast may show again), whereas trusting garbage
 * could suppress a toast the user has never seen — the worse failure.
 * @param {string | null | undefined} raw - The stored JSON, if any.
 * @param {Date} now - Current wall-clock time.
 * @returns {Map<string, number>} toastKey -> expiresAt in epoch ms, unexpired only.
 */
export function parseSeenToasts(
  raw: string | null | undefined,
  now: Date
): Map<string, number> {
  const expiry = new Map<string, number>();
  if (typeof raw !== "string" || raw.trim() === "") return expiry;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return expiry;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return expiry;
  }

  const nowMs = now.getTime();
  for (const [key, value] of Object.entries(
    parsed as Record<string, unknown>
  )) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    if (value <= nowMs) continue;
    expiry.set(key, value);
  }
  return expiry;
}

/**
 * @param {ReadonlyMap<string, number>} expiry - toastKey -> expiresAt in epoch ms.
 * @returns {string} JSON for storage.
 */
export function serializeSeenToasts(
  expiry: ReadonlyMap<string, number>
): string {
  return JSON.stringify(Object.fromEntries(expiry));
}

/**
 * Reads this browser's already-shown automation toasts.
 *
 * Not scoped by shop: a toast key embeds the card id, which is globally
 * unique, so scoping would buy nothing and would force a re-hydrate on shop
 * switch. Entries self-prune inside the 5-minute eligibility window.
 * @param {Date} now - Current wall-clock time.
 * @returns {object} `keys` for selectPendingToasts, `expiry` for pruning.
 */
export function loadSeenToasts(now: Date): {
  keys: Set<string>;
  expiry: Map<string, number>;
} {
  let raw: string | null | undefined = null;
  try {
    raw = localStorageManager.getItem(STORAGE_KEY);
  } catch {
    // Storage disabled (private mode, blocked cookies): in-memory only.
    raw = null;
  }
  const expiry = parseSeenToasts(raw, now);
  return { keys: new Set(expiry.keys()), expiry };
}

/**
 * Writes this browser's already-shown automation toasts.
 *
 * Failures are swallowed: a full quota or disabled storage must degrade to
 * in-memory-only dedupe, never throw inside the manager's render effect.
 * @param {ReadonlyMap<string, number>} expiry - toastKey -> expiresAt in epoch ms.
 */
export function persistSeenToasts(expiry: ReadonlyMap<string, number>): void {
  try {
    localStorageManager.setItem(STORAGE_KEY, serializeSeenToasts(expiry));
  } catch {
    // Intentionally ignored — see the doc comment above.
  }
}

/**
 * Seeds in-memory seen-toast bookkeeping from storage, additively.
 *
 * Additive on purpose: a toast shown earlier in this same page load is already
 * in `keys`, and replacing the set would re-offer it the moment hydration runs.
 * @param {Set<string>} keys - The seen-key set `selectPendingToasts` checks. Mutated.
 * @param {Map<string, number>} expiry - Side table of key -> expiresAt (ms). Mutated.
 * @param {Date} now - Current wall-clock time; expired entries are not seeded.
 */
export function hydrateInto(
  keys: Set<string>,
  expiry: Map<string, number>,
  now: Date
): void {
  const stored = loadSeenToasts(now);
  stored.keys.forEach((key) => keys.add(key));
  stored.expiry.forEach((expiresAtMs, key) => expiry.set(key, expiresAtMs));
}
